import * as vp from 'vopay';

import { FunctionComponent, useContext, useState, useEffect, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import AccountCreationContext from './AccountCreationContext';
import MultiStepSection from './MultiStepSection';
import MultiStepReview from './MultiStepReview';
import ElinxConnect, { EventMessageData } from '../../ElinxConnect/ElinxConnect';
import InteracInfoContext from './Interac/InteracInfoContext';
import { useClientData } from '../../../providers/ClientData.provider';
import useFetchFinancialInstitutionBranches from '@/hooks/useApi/useFetchFinancialInstitutionBranches';
import useFetchFinancialInstitutions from '@/hooks/useApi/useFetchFinancialInstitutions';

const { MultiStep, TermsAndConditions } = vp;
export type AccountCreationProps = {
  extraSteps: {
    label: string;
    content: JSX.Element;
    showStep: () => boolean;
    hasError: () => boolean;
    percentageComplete: (idx: number) => number;
  }[];
  onStepChange: (idx: number) => void;
  onStepReject: (idx: number) => void;
  onTeneativeSubmit?: () => void;
  onCompleteToken?: (token: string, paymentMethod: string) => void;
  onCompleteTokenSecondBank?: (token: string, paymentMethod: string) => void;
  stepUser: number;
  stepUserChange: boolean;
  customReview?: JSX.Element;
  showSave: boolean;
  isActive: boolean;
  isAllowMove: boolean;
  isAllowPaylink: boolean;
  isAllow2Banks: boolean;
  title: string;
  isClientControlled: boolean;
  defaultValues?: {
    currency: string;
    paymentMethod: string;
    country: string;
    ClientAccountsEnabled?: string;
    clientAccount?: string;
  };
};

type EventMessage = {
  data: EventMessageData;
};
const AccountCreationForm: FunctionComponent<AccountCreationProps> = (props: AccountCreationProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'AccountCreation' });
  const ctx = useContext(AccountCreationContext);
  const { getCompletionPercentage, hasError } = ctx.useFields();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPayLinkEnabled, setIsPayLinkEnabled] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { currency, country } = ctx.values;
  const { agreeToTermsAndConditions } = ctx.values;
  const InteracCtx = useContext(InteracInfoContext);
  const RecipientName = InteracCtx.interacDetails?.RecipientName;
  const [financialInstitutionsResponse, fetchFinancialInstitutions] = useFetchFinancialInstitutions({});
  const [, , , fetchFinancialInstitutionBranches] = useFetchFinancialInstitutionBranches({});
  const {
    response: { ClientAccountsEnabled, Country },
  } = useClientData();
  const {
    extraSteps,
    customReview,
    isActive,
    stepUser,
    stepUserChange,
    onStepChange,
    isAllowMove,
    isAllowPaylink,
    onStepReject,
    onTeneativeSubmit,
    onCompleteToken,
    onCompleteTokenSecondBank,
    defaultValues,
    title,
    isAllow2Banks,
    isClientControlled,
    showSave,
  } = props;
  const elinxConnectCreditSettings: vp.EmbedUrlRequest = {
    RedirectURL: 'https://vopay.com',
    Country,
    PaymentSelectionMethod: 'credit',
    ClientControlled: isClientControlled,
    RedirectMethod: 'JavascriptMessage',
    ClientAccountID: defaultValues?.clientAccount,
  };
  const elinxConnectBankSettings: vp.EmbedUrlRequest = {
    RedirectURL: 'https://vopay.com',
    Country,
    PaymentSelectionMethod: 'bank',
    ClientControlled: isClientControlled,
    RedirectMethod: 'JavascriptMessage',
    ClientAccountID: defaultValues?.clientAccount,
  };
  useEffect(() => {
    fetchFinancialInstitutions({});
  }, []);

  useEffect(() => {
    if (financialInstitutionsResponse.Success) {
      const options: { [key: string]: string } = {};
      Object.values(financialInstitutionsResponse.FinancialInstitutions || {}).forEach((fi) => {
        options[fi.FinancialInstitutionNumber] = `${fi.FinancialInstitutionNumber} <br /> ${fi.FIName}`;
      });
      ctx.setFieldValue('FinancialInstitutions', options);
    }
  }, [financialInstitutionsResponse]);

  useEffect(() => {
    const fetchBranches = async () => {
      if (
        ctx.values.FinancialInstitutionNumber &&
        ctx.values.FinancialInstitutionNumber !== '' &&
        !ctx.values.branchTransitManual
      ) {
        const branchesResponse = await fetchFinancialInstitutionBranches({
          FinancialInstitutionID: Number(ctx.values.FinancialInstitutionNumber),
        });
        const options: { [key: string]: string } = {};
        Object.values(branchesResponse.Branches || {}).forEach((branches) => {
          options[branches.BranchTransitNumber] =
            `${branches.BranchTransitNumber} <br /> ${branches.Address}, ${branches.TownCity}, ${branches.PostalCode}, ${branches.Province}`;
        });
        ctx.setFieldValue('BranchTransitNumbers', options);
      } else {
        ctx.setFieldValue('BranchTransitNumbers', {});
      }
    };
    fetchBranches();
  }, [ctx.values.FinancialInstitutionNumber]);

  const onClosePaymentMethod = () => {
    setCurrentStep(currentStep - 1);
  };

  const onConnectPaymentMethod = (event: EventMessage) => {
    setCurrentStep(currentStep + 1);
    if (onCompleteToken) {
      onCompleteToken(event.data.Token, event.data.PaymentMethod);
    }
  };
  const onConnectPaymentMethod2 = (event: EventMessage) => {
    setCurrentStep(currentStep + 1);
    if (onCompleteTokenSecondBank) {
      onCompleteTokenSecondBank(event.data.Token, event.data.PaymentMethod);
    }
  };
  const steps = [
    ...extraSteps,
    {
      label: '',
      content: <MultiStepSection showSubLabels sectionId="contactSettings" />,
      showStep: () => extraSteps.length === 0,
      hasError: () => hasError('contactSettings', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('contactSettings', ctx, currentStep >= idx),
    },
    {
      label: '',
      content: <MultiStepSection showSubLabels sectionId="contactDetails" />,
      showStep: () =>
        ctx.values.paymentMethod === 'bank' && defaultValues?.ClientAccountsEnabled === '1' ? false : isActive,
      hasError: () => hasError('contactDetails', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('contactDetails', ctx, currentStep >= idx),
    },

    {
      label: '',
      content: <MultiStepSection showSubLabels sectionId="accountDetails" />,
      showStep: () =>
        ctx.values.paymentMethod !== 'e-transfer' &&
        !isClientControlled &&
        isPayLinkEnabled &&
        isAllowPaylink &&
        !(extraSteps.length === 0 && vp.toBool(ClientAccountsEnabled)) &&
        isActive,

      hasError: () => hasError('accountDetails', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('accountDetails', ctx, currentStep >= idx),
    },
    {
      label: '',
      content: <MultiStepSection showSubLabels sectionId="accountDetailsEtransfer" />,
      showStep: () => ctx.values.paymentMethod === 'e-transfer' && isActive,
      hasError: () => hasError('accountDetailsEtransfer', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('accountDetailsEtransfer', ctx, currentStep >= idx),
    },
    {
      label: '',
      content: (
        <>
          <ElinxConnect
            setting={elinxConnectCreditSettings}
            onSuccess={onConnectPaymentMethod}
            onClose={onClosePaymentMethod}
          />
        </>
      ),
      showStep: () => ctx.values.paymentMethod === 'creditCard' && ctx.values.connectionType === 'Manual' && isActive,
      hasError: () => hasError('tokenDetails', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('tokenDetails', ctx, currentStep >= idx),
    },
    {
      label: '',
      content: <MultiStepSection showSubLabels sectionId="provideDetailsBankAccount" />,
      showStep: () =>
        ctx.values.paymentMethod === 'bank' &&
        ctx.values.connectionType === 'Manual' &&
        !isClientControlled &&
        isActive,
      hasError: () => hasError('provideDetailsBankAccount', ctx),
      percentageComplete: (idx: number) =>
        getCompletionPercentage('provideDetailsBankAccount', ctx, currentStep >= idx),
    },
    {
      label: '',
      content: (
        <>
          <ElinxConnect
            setting={elinxConnectBankSettings}
            onSuccess={onConnectPaymentMethod}
            onClose={onClosePaymentMethod}
          />
        </>
      ),
      showStep: () =>
        ctx.values.paymentMethod === 'bank' && ctx.values.connectionType === 'Manual' && isClientControlled && isActive,
      hasError: () => hasError('tokenDetails', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('tokenDetails', ctx, currentStep >= idx),
    },
    {
      label: '',
      content: <MultiStepSection showSubLabels sectionId="contactDetailsSecondBank" />,
      showStep: () => isActive && isAllow2Banks,
      hasError: () => hasError('contactDetailsSecondBank', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('contactDetailsSecondBank', ctx, currentStep >= idx),
    },
    {
      label: '',
      content: (
        <>
          <ElinxConnect
            setting={elinxConnectBankSettings}
            onSuccess={onConnectPaymentMethod2}
            onClose={onClosePaymentMethod}
          />
        </>
      ),
      showStep: () =>
        ctx.values.paymentMethod === 'bank' &&
        ctx.values.connectionType === 'Manual' &&
        isActive &&
        isAllow2Banks &&
        isClientControlled,
      hasError: () => hasError('tokenDetails', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('tokenDetails', ctx, currentStep >= idx),
    },
    {
      label: '',
      content: <MultiStepSection showSubLabels sectionId="connectionRequest" />,
      showStep: () =>
        (ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
        ctx.values.connectionType !== 'Manual' &&
        isActive,
      hasError: () => hasError('connectionRequest', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('connectionRequest', ctx, currentStep >= idx),
    },
    {
      label: t('reviewAndSubmit', { defaultValue: 'Review and Submit' }),
      content: customReview || <MultiStepReview />,
      showStep: () => true,
      hasError: () => false,
      percentageComplete: () => 100,
    },
  ];

  const handleStepChange = (stepIdx: number) => {
    setCurrentStep(stepIdx);
    onStepChange(stepIdx);
  };

  const handleAgreeBtn = () => {
    setIsModalVisible(false);
  };

  const handleDisagreeBtn = (value: boolean) => {
    ctx.handleFieldChange(vp.createSyntheticEvent('agreeToTermsAndConditions', value));
    setIsModalVisible(false);
  };

  useEffect(() => {
    if (currency === 'CAD') {
      setIsPayLinkEnabled(true);
    } else {
      setIsPayLinkEnabled(false);
    }
  }, [currency]);

  useEffect(() => {
    if (agreeToTermsAndConditions) {
      setIsModalVisible(true);
    }
  }, [agreeToTermsAndConditions]);
  useEffect(() => {
    if (stepUser !== null) {
      setCurrentStep(stepUser);
      onStepChange(stepUser);
    }
  }, [stepUserChange, stepUser]);
  useEffect(() => {
    ctx.handleFieldChange(vp.createSyntheticEvent('paymentMethod', defaultValues?.paymentMethod));
    ctx.handleFieldChange(vp.createSyntheticEvent('currency', defaultValues?.currency));
    ctx.handleFieldChange(vp.createSyntheticEvent('country', defaultValues?.country));
    if (defaultValues?.country === 'US' && !vp.toBool(ClientAccountsEnabled)) {
      ctx.handleFieldChange(vp.createSyntheticEvent('saveAccountInformation', false));
    }
  }, [defaultValues]);
  useEffect(() => {
    ctx.handleFieldChange(vp.createSyntheticEvent('isAllow2Banks', isAllow2Banks));
  }, [isAllow2Banks]);
  useEffect(() => {
    ctx.handleFieldChange(vp.createSyntheticEvent('isClientControlled', isClientControlled));
  }, [isClientControlled]);
  useEffect(() => {
    if (ctx.values.isClientControlled !== isClientControlled) {
      ctx.handleFieldChange(vp.createSyntheticEvent('isClientControlled', isClientControlled));
    }
  }, [ctx.values.isClientControlled]);
  useEffect(() => {
    ctx.handleFieldChange(vp.createSyntheticEvent('showSave', showSave && defaultValues?.country !== 'US'));
  }, [showSave, isAllowMove]);
  useEffect(() => {
    if (ctx.values.emailAddress) {
      ctx.handleFieldChange(vp.createSyntheticEvent('paylinkEmailAddress', ctx.values.emailAddress));
    }
  }, [ctx.values.emailAddress]);
  useEffect(() => {
    ctx.handleFieldChange(vp.createSyntheticEvent('RecipientName', RecipientName));

    ctx.handleFieldChange(
      vp.createSyntheticEvent(
        'etransferConnectionType',
        InteracCtx.interacDetails?.RecipientPhoneNumber !== '' ? 'phone' : 'email',
      ),
    );
    ctx.handleFieldChange(
      vp.createSyntheticEvent('RecipientEmailAddress', InteracCtx?.interacDetails?.RecipientEmailAddress || ''),
    );
    ctx.handleFieldChange(
      vp.createSyntheticEvent('paylinkEmailAddress', InteracCtx?.interacDetails?.RecipientEmailAddress || ''),
    );

    ctx.handleFieldChange(
      vp.createSyntheticEvent('RecipientPhoneNumber', InteracCtx?.interacDetails?.RecipientPhoneNumber || ''),
    );
    ctx.handleFieldChange(vp.createSyntheticEvent('Answer', InteracCtx?.interacDetails?.Answer || ''));
    ctx.handleFieldChange(vp.createSyntheticEvent('Question', InteracCtx?.interacDetails?.Question || ''));
    ctx.handleFieldChange(
      vp.createSyntheticEvent('clientReferenceNumber', InteracCtx?.interacDetails?.ClientReferenceNumber || ''),
    );
    if (!InteracCtx?.interacDetails?.Question) {
      ctx.setFieldTouched('Question', false);
    }

    if (!InteracCtx?.interacDetails?.Answer) {
      ctx.setFieldTouched('Answer', false);
    }

    if (!InteracCtx?.interacDetails?.Question) {
      ctx.setFieldTouched('Answer', false);
    }

    if (!InteracCtx?.interacDetails?.RecipientPhoneNumber && !InteracCtx?.interacDetails?.RecipientEmailAddress) {
      ctx.setFieldTouched('RecipientName', false);
      ctx.setFieldTouched('RecipientEmailAddress', false);
    }
    if (!InteracCtx?.interacDetails?.ClientReferenceNumber) {
      ctx.setFieldTouched('ClientReferenceNumber', false);
    }
    if (!RecipientName) {
      ctx.setFieldTouched('RecipientName', false);
    }
  }, [InteracCtx]);
  return (
    <>
      <TermsAndConditions
        isVisible={isModalVisible}
        country={country || 'CA'}
        onAgree={handleAgreeBtn}
        onDisagree={handleDisagreeBtn}
      />
      <MultiStep
        id="create-account"
        title={title}
        stepUser={currentStep}
        // steps={getSteps()}
        steps={steps}
        onStepChange={handleStepChange}
        onStepReject={onStepReject}
        handlePaylinkStep={() => {}}
        isPayLinkEnabled={false}
        isEmbeded={extraSteps.length !== 0}
        isAllowMove={isAllowMove}
        onTeneativeSubmit={onTeneativeSubmit}
      />
    </>
  );
};

export default AccountCreationForm;
